import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { InvitationOutcome, InvitationStatus, InvitationView } from '../lib/admin-invitations';
import { absolute } from '../lib/time';
import { AdminInvitationActions } from './AdminInvitationActions';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill, type StatusTone } from './StatusPill';

// The admin invitations list as a dense data table (spec §3.15). Presenter —
// the panel owns the accumulated rows, the filter and pagination; this paints
// one row per invitation, and `<AdminInvitationActions/>` owns each row's writes.
// `status` is the SERVER's answer, resolved against its clock at read.

/** Status → pill tone + label key. Literal keys so `i18n.coverage` sees each. */
const STATUS: Readonly<Record<InvitationStatus, { key: string; tone: StatusTone }>> = {
  pending: { key: 'admin.invitation_status_pending', tone: 'warning' },
  accepted: { key: 'admin.invitation_status_accepted', tone: 'success' },
  expired: { key: 'admin.invitation_status_expired', tone: 'neutral' },
  revoked: { key: 'admin.invitation_status_revoked', tone: 'danger' },
};

export function AdminInvitationsTable(props: {
  readonly rows: readonly InvitationView[];
  readonly token: string | null;
  readonly onOutcome: (outcome: InvitationOutcome) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<InvitationView>[] = [
    {
      key: 'email',
      rowHeader: true,
      header: t('admin.invitations_col_email'),
      mono: true,
      cell: (row) => row.email,
    },
    {
      key: 'kind',
      header: t('admin.invitations_col_kind'),
      cell: (row) =>
        row.kind === 'platform'
          ? t('admin.invitation_kind_platform')
          : t('admin.invitation_kind_org_member'),
    },
    {
      key: 'status',
      header: t('admin.invitations_col_status'),
      cell: (row) => (
        <StatusPill tone={STATUS[row.status].tone}>{t(STATUS[row.status].key)}</StatusPill>
      ),
    },
    {
      key: 'lifetime',
      header: t('admin.invitations_col_lifetime'),
      cell: (row) => (
        <div class="admin-row__stack">
          <p class="admin-row__meta">
            {t('admin.invitation_sent_at', { at: absolute(row.created_at_iso) })}
          </p>
          <Show when={row.status === 'pending'}>
            <p class="admin-row__meta">
              {t('admin.invitation_expires_at', { at: absolute(row.expires_at_iso) })}
            </p>
          </Show>
          <Show when={row.accepted_at_iso}>
            {(at) => (
              <p class="admin-row__meta">
                {t('admin.invitation_accepted_at', { at: absolute(at()) })}
              </p>
            )}
          </Show>
          <Show when={row.revoked_at_iso}>
            {(at) => (
              <p class="admin-row__meta">
                {t('admin.invitation_revoked_at', { at: absolute(at()) })}
              </p>
            )}
          </Show>
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('admin.col_actions'),
      align: 'end',
      cell: (row) => (
        <AdminInvitationActions row={row} token={props.token} onOutcome={props.onOutcome} />
      ),
    },
  ];

  return (
    <DataTable
      label={t('admin.invitations_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
